import { useState } from "react";
import { Download, Calendar, MapPin, FileText, MessageCircle, ChevronRight } from "lucide-react";
import { LOCANDINE, WHATSAPP_URL } from "@/lib/constants";

const catColor = {
  Slalom: "bg-legend-yellow text-legend-ink",
  Rally: "bg-slate-700 text-white",
  Storiche: "bg-amber-700 text-white",
};

export default function Locandina() {
  const current = LOCANDINE.find((l) => l.current) || LOCANDINE[0];
  const archive = LOCANDINE;
  const [active, setActive] = useState(current);

  return (
    <div className="max-w-6xl mx-auto px-4 py-10" data-testid="locandina-page">
      <div className="mb-8">
        <span className="inline-flex items-center gap-2 font-mono uppercase text-xs tracking-[0.3em] text-legend-red mb-2"><FileText size={14} /> Locandine ufficiali</span>
        <h1 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-none">Le Locandine degli Eventi</h1>
        <p className="text-slate-500 text-base mt-3 max-w-2xl">Sfoglia le locandine ufficiali degli eventi — attuali e passate. Seleziona una miniatura per aprire e scaricare il PDF pronto da stampare e condividere.</p>
      </div>

      <div className="grid lg:grid-cols-[1.15fr_1fr] gap-8">
        {/* Featured / selected poster */}
        <div className="lg:sticky lg:top-20 h-fit">
          <div className="rounded-sm border border-slate-200 dark:border-slate-800 bg-white dark:bg-legend-paper overflow-hidden shadow-xl">
            <a href={active.pdf} target="_blank" rel="noreferrer" data-testid="locandina-featured-open" className="block bg-slate-100 dark:bg-black">
              <img src={active.thumb} alt={active.title} className="w-full object-contain max-h-[70vh] mx-auto" />
            </a>
            <div className="p-5">
              <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm ${catColor[active.category] || catColor.Rally}`}>{active.category}</span>
              <h2 className="font-display font-black uppercase text-2xl leading-tight mt-2">{active.title}</h2>
              <p className="text-sm text-slate-500 flex items-center gap-2 mt-1"><MapPin size={14} className="text-legend-red" /> {active.location}</p>
              <p className="text-sm text-slate-500 flex items-center gap-2"><Calendar size={14} className="text-legend-red" /> {active.date_label}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <a href={active.pdf} download target="_blank" rel="noreferrer" data-testid="download-locandina-btn" className="inline-flex items-center gap-2 bg-legend-red hover:bg-legend-dark text-white font-bold uppercase tracking-wide px-6 py-3 rounded-sm transition-colors">
                  <Download size={18} /> Scarica PDF A4
                </a>
                {active.slug === "sogliano-2026" && (
                  <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" data-testid="whatsapp-info-btn" className="inline-flex items-center gap-2 bg-[#25D366] hover:brightness-95 text-black font-bold uppercase tracking-wide px-5 py-3 rounded-sm transition">
                    <MessageCircle size={16} /> Info su WhatsApp
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Archive list — F1/rally style */}
        <div>
          <h3 className="font-display font-bold uppercase text-lg text-legend-red mb-3 flex items-center gap-2"><FileText size={16} /> Archivio locandine</h3>
          <div className="space-y-2">
            {archive.map((l) => (
              <button
                key={l.slug}
                onClick={() => setActive(l)}
                data-testid={`locandina-row-${l.slug}`}
                className={`w-full text-left p-3 rounded-sm border transition-all flex items-center gap-4 ${active.slug === l.slug ? "border-legend-red bg-legend-red/5" : "border-slate-200 dark:border-slate-800 hover:border-legend-red bg-white dark:bg-legend-paper"}`}
              >
                <img src={l.thumb} alt={l.title} className="w-14 h-20 object-cover rounded-sm border border-slate-200 dark:border-slate-700 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm ${catColor[l.category] || catColor.Rally}`}>{l.category}</span>
                    {l.current && <span className="text-[10px] font-mono text-legend-red font-bold uppercase">Attuale</span>}
                  </div>
                  <h4 className="font-display font-bold uppercase leading-tight mt-1 truncate">{l.title}</h4>
                  <p className="text-xs text-slate-500 flex items-center gap-1"><Calendar size={11} /> {l.date_label}</p>
                </div>
                <ChevronRight size={16} className="text-slate-400 shrink-0" />
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-4">Nei prossimi mesi l'archivio si arricchirà con le locandine passate e con i nuovi eventi in calendario.</p>
        </div>
      </div>
    </div>
  );
}
